// "New cycle" — the form that states a reporting period and creates the cycle every other
// surface in the island hangs off.
//
// THE SHAPE OF THIS FORM IS THE BUSINESS RULE:
//
//   * period_type is the ONLY real choice. Everything else follows from it and the label.
//   * is_webinar_month is DERIVED and READ-ONLY. Sam's rule — "webinar month will be the
//     same as quarterly month" — so it is a function of period_type, shown with its
//     derivation stated. There is deliberately no checkbox: a checkbox would let the two
//     drift, and is_webinar_month decides whether the webinar-gated deck kinds are required
//     to be frozen before the gate can be signed (GateTab.deriveRenderBinding).
//   * The default is monthly-interim on last month, because that is the case ten months of
//     the year: during a quarter you report the month just ended; after a quarter ends you
//     report the quarter.
//   * The COMPUTE WINDOW is derived from the label but SENT EXPLICITLY, because
//     create_reporting_cycle refuses to create a cycle without it and never infers it (it
//     defines the trade-date window, the temporal quarantine and the R day-count
//     time-weighting). It is shown, explained, and overridable — not hidden.
//
// Every validation here is a mirror of one the RPC performs. The RPC is the wall; this only
// turns its exceptions into sentences a human can act on.

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, CalendarPlus, Lock } from "lucide-react";
import { useCreateReportingCycle } from "@/hooks/investment-reporting/useCreateReportingCycle";
import {
  PERIOD_LABEL_HELP,
  PERIOD_TYPES,
  WEBINAR_DERIVATION_NOTE,
  derivePeriodWindow,
  deriveIsWebinarMonth,
  priorPeriodEnd,
  suggestPeriodLabel,
  validateNewCycle,
  type ReportingPeriodType,
} from "@/lib/investment-reporting/period";

export function NewReportingCycleDialog({
  canCreate,
  onCreated,
}: {
  /** False for admins — the RPC is Investment/Operations only and will refuse them. */
  canCreate: boolean;
  onCreated?: (cycleId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [periodType, setPeriodType] = useState<ReportingPeriodType>("monthly-interim");
  const [periodLabel, setPeriodLabel] = useState<string>(() => suggestPeriodLabel("monthly-interim"));

  // as_of_date and the window are DERIVED from the label until a human overrides them; the
  // `touched` flags are what make the inputs stop re-seeding. Changing the period type or
  // label resets both, so the form never silently keeps a window from a different period.
  const [asOfInput, setAsOfInput] = useState("");
  const [asOfTouched, setAsOfTouched] = useState(false);
  const [winStartInput, setWinStartInput] = useState("");
  const [winEndInput, setWinEndInput] = useState("");
  const [windowTouched, setWindowTouched] = useState(false);
  const [showWindow, setShowWindow] = useState(false);

  const create = useCreateReportingCycle();

  const derived = useMemo(() => derivePeriodWindow(periodLabel), [periodLabel]);
  const isWebinarMonth = deriveIsWebinarMonth(periodType);

  const asOfDate = asOfTouched ? asOfInput : (derived?.as_of_date ?? "");
  // Unless the window has been hand-edited, a quarterly window ENDS where the as-of date
  // does — which is exactly the invariant the RPC enforces, satisfied by construction.
  const windowStart = windowTouched ? winStartInput : (derived?.window_start ?? "");
  const windowEnd = windowTouched
    ? winEndInput
    : periodType === "quarterly-full"
      ? asOfDate
      : (derived?.window_end ?? "");

  // Display only — never sent. The return datum is the PREVIOUS period's close, so an FE
  // export for July runs 30 Jun → 31 Jul while the window is 1 Jul → 31 Jul. Both are right;
  // the ingest derives the datum itself as window_start − 1. Surfaced here because the
  // mismatch reads as an off-by-one to anyone who measures returns, and "correcting" the
  // window would make the ingest expect the day before the datum and reject a valid file.
  const datumDate = priorPeriodEnd(windowStart);

  const problems = validateNewCycle({
    period_type: periodType,
    period_label: periodLabel,
    as_of_date: asOfDate,
    window_start: windowStart,
    window_end: windowEnd,
  });

  function resetDerivations() {
    setAsOfTouched(false);
    setWindowTouched(false);
    setAsOfInput("");
    setWinStartInput("");
    setWinEndInput("");
  }

  function pickPeriodType(next: string) {
    const t = next as ReportingPeriodType;
    setPeriodType(t);
    setPeriodLabel(suggestPeriodLabel(t));
    resetDerivations();
    create.reset();
  }

  function pickPeriodLabel(next: string) {
    setPeriodLabel(next);
    resetDerivations();
    create.reset();
  }

  // Opening the dialog always starts from the defaults for TODAY — never from whatever was
  // left in the form last time, which would be a different period.
  function resetForm() {
    setPeriodType("monthly-interim");
    setPeriodLabel(suggestPeriodLabel("monthly-interim"));
    setShowWindow(false);
    resetDerivations();
    create.reset();
  }

  function beginWindowEdit() {
    setWinStartInput(windowStart);
    setWinEndInput(windowEnd);
    setWindowTouched(true);
    setShowWindow(true);
  }

  async function submit() {
    if (problems.length || !canCreate) return;
    try {
      const id = await create.mutateAsync({
        periodLabel: periodLabel.trim(),
        periodType,
        asOfDate,
        windowStart,
        windowEnd,
        isWebinarMonth,
      });
      setOpen(false);
      if (id && onCreated) onCreated(id);
    } catch {
      /* surfaced from create.error below — never a silent failure */
    }
  }

  const typeSpec = PERIOD_TYPES.find((t) => t.value === periodType);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) resetForm();
        else create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" disabled={!canCreate}>
          <CalendarPlus className="h-3.5 w-3.5 mr-1.5" aria-hidden />
          New cycle
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">New reporting cycle</DialogTitle>
          <DialogDescription className="font-body">
            State the period. Everything downstream — the golden-CSV drops, the compute window and
            the gate — keys off what you enter here.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 font-body">
          {/* Period type — the only real choice on this form */}
          <div className="space-y-1.5">
            <Label className="text-sm">Period type</Label>
            <Select value={periodType} onValueChange={pickPeriodType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {typeSpec && <p className="text-[11px] text-muted-foreground">{typeSpec.hint}</p>}
          </div>

          {/* Period label */}
          <div className="space-y-1.5">
            <Label className="text-sm" htmlFor="ir-period-label">
              Period label
            </Label>
            <Input
              id="ir-period-label"
              value={periodLabel}
              onChange={(e) => pickPeriodLabel(e.target.value)}
              placeholder={suggestPeriodLabel(periodType)}
              className="font-mono"
            />
            <p className="text-[11px] text-muted-foreground">{PERIOD_LABEL_HELP}</p>
          </div>

          {/* As-of date */}
          <div className="space-y-1.5">
            <Label className="text-sm" htmlFor="ir-as-of">
              As-of date
            </Label>
            <Input
              id="ir-as-of"
              type="date"
              value={asOfDate}
              onChange={(e) => {
                setAsOfInput(e.target.value);
                setAsOfTouched(true);
              }}
            />
            <p className="text-[11px] text-muted-foreground">
              Defaults to the last day of the labelled period. This is the snapshot horizon.
            </p>
          </div>

          {/* Derived facts — read-only, with their derivations stated */}
          <Card className="p-3 space-y-2.5 bg-muted/30">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Lock className="h-3 w-3 text-muted-foreground" aria-hidden />
                  <span className="text-xs font-medium text-foreground">Webinar month</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{WEBINAR_DERIVATION_NOTE}</p>
              </div>
              <span
                className={
                  isWebinarMonth
                    ? "text-sm font-medium text-primary shrink-0"
                    : "text-sm font-medium text-muted-foreground shrink-0"
                }
              >
                {isWebinarMonth ? "Yes" : "No"}
              </span>
            </div>

            <div className="border-t pt-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-xs font-medium text-foreground">Compute window</span>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Derived from the label. Sent explicitly because it is never inferred later — it
                    defines the trade-date window, the temporal quarantine and the R time-weighting.
                  </p>
                  {/* The window start is the FIRST DAY OF THE PERIOD, not the return datum. Those
                      are different dates doing different jobs, and the difference reliably looks
                      like an off-by-one to anyone who measures returns for a living: a July return
                      is struck from the 30 June close, so an FE export for July spans 30 Jun ->
                      31 Jul, not 1 Jul -> 31 Jul.
                      Both are correct. reporting-golden-ingest derives the datum itself —
                      `expected_from = isoAddDays(window_start, -1)` (index.ts:1242, and see the
                      note at :71 "`from` is the PRIOR period end, i.e. window_start - 1 day.
                      Coding `from == window_start` false-fails EVERY export").
                      So "correcting" this field to the prior period end would make the ingest
                      expect 29 June and reject a perfectly good file. Stating the derived range
                      here is cheaper than letting someone discover that by having their upload
                      refused. */}
                  {datumDate && windowEnd && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Your FE export will span{" "}
                      <span className="font-medium text-foreground">
                        {datumDate} → {windowEnd}
                      </span>
                      : returns are struck from the previous period&rsquo;s close, so the file starts
                      one day earlier than the window. The ingest derives that datum itself — leave
                      the window as the period.
                    </p>
                  )}
                </div>
                {!showWindow && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[11px] shrink-0"
                    onClick={beginWindowEdit}
                  >
                    Adjust
                  </Button>
                )}
              </div>

              {showWindow ? (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground" htmlFor="ir-win-start">
                      Window start
                    </Label>
                    <Input
                      id="ir-win-start"
                      type="date"
                      className="h-8 text-xs"
                      value={windowStart}
                      onChange={(e) => {
                        setWinStartInput(e.target.value);
                        setWindowTouched(true);
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground" htmlFor="ir-win-end">
                      Window end
                    </Label>
                    <Input
                      id="ir-win-end"
                      type="date"
                      className="h-8 text-xs"
                      value={windowEnd}
                      onChange={(e) => {
                        setWinEndInput(e.target.value);
                        setWindowTouched(true);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-foreground mt-1 tabular-nums">
                  {windowStart || "—"} → {windowEnd || "—"}
                </p>
              )}
            </div>
          </Card>

          {/* Problems — the RPC's own refusals, restated in advance */}
          {problems.length > 0 && (
            <ul className="space-y-1">
              {problems.map((p) => (
                <li key={p} className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}

          {create.error && (
            <p className="text-xs text-destructive">
              {(create.error as any)?.message ?? "The cycle could not be created."}
            </p>
          )}

          {!canCreate && (
            <p className="text-xs text-muted-foreground">
              Creating a cycle is an Investment or Operations action. That is narrower than signing
              the gate, which admins may now do.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!canCreate || problems.length > 0 || create.isPending}>
            {create.isPending ? "Creating…" : "Create cycle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
